import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah } from "@/lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trophy, Crown, Medal, Target, TrendingUp, Flame, Award } from "lucide-react";

const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

const BADGE_META = {
  top_omset: { label: "Raja Omset", icon: Crown, cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  top_closing: { label: "Closer Terbanyak", icon: Flame, cls: "bg-red-500/15 text-red-400 border-red-500/30" },
  top_rate: { label: "Konversi Terbaik", icon: TrendingUp, cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  target_hit: { label: "Target Tercapai", icon: Target, cls: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
};

const RANK_STYLE = {
  1: { ring: "ring-amber-400/60", grad: "from-amber-400 to-yellow-500", icon: Crown, label: "Juara 1" },
  2: { ring: "ring-zinc-300/50", grad: "from-zinc-300 to-zinc-400", icon: Medal, label: "Juara 2" },
  3: { ring: "ring-orange-500/50", grad: "from-orange-400 to-amber-600", icon: Medal, label: "Juara 3" },
};

function Badges({ badges }) {
  if (!badges?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {badges.map((b) => {
        const m = BADGE_META[b]; if (!m) return null; const Icon = m.icon;
        return (
          <span key={b} data-testid={`badge-${b}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${m.cls}`}>
            <Icon size={11} />{m.label}
          </span>
        );
      })}
    </div>
  );
}

export default function Leaderboard() {
  const { user } = useAuth();
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const { data, isLoading } = useQuery({
    queryKey: ["leaderboard", month],
    queryFn: async () => (await api.get(`/leaderboard?month=${month}`)).data,
  });

  const rows = data?.rows || [];
  const podium = [rows[1], rows[0], rows[2]].filter(Boolean); // 2nd, 1st, 3rd for center-tall layout
  const [y, m] = month.split("-");
  const monthOpts = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    monthOpts.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }

  return (
    <div className="px-4 py-5 max-w-5xl mx-auto md:max-w-none md:px-8" data-testid="leaderboard-page">
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div>
          <h1 className="text-xl font-bold font-heading tracking-tight flex items-center gap-2">
            <Trophy className="text-amber-400" size={22} />Leaderboard Sales
          </h1>
          <p className="text-sm text-zinc-500">Peringkat performa bulanan — {MONTHS[parseInt(m, 10) - 1]} {y}</p>
        </div>
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger data-testid="lb-month-select" className="w-44 bg-[#18181B] border-white/10 h-10"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#18181B] border-white/10">
            {monthOpts.map((mo) => { const [yy, mm] = mo.split("-"); return (
              <SelectItem key={mo} value={mo}>{MONTHS[parseInt(mm, 10) - 1]} {yy}</SelectItem>
            ); })}
          </SelectContent>
        </Select>
      </div>

      {isLoading && <div className="text-sm text-zinc-500 py-10 text-center">Memuat…</div>}

      {!isLoading && rows.length === 0 && (
        <div className="card-surface rounded-2xl p-10 text-center text-zinc-500">Belum ada data sales untuk periode ini.</div>
      )}

      {/* Podium */}
      {!isLoading && podium.length > 0 && (
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6 items-end" data-testid="lb-podium">
          {podium.map((r) => {
            const st = RANK_STYLE[r.rank]; const Icon = st.icon;
            const tall = r.rank === 1;
            return (
              <div key={r.sales_id} className={`card-surface rounded-2xl p-3 sm:p-4 text-center flex flex-col items-center ${tall ? "pt-6" : "pt-4"} ring-1 ${st.ring}`}>
                <div className={`w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-gradient-to-tr ${st.grad} flex items-center justify-center mb-2 shadow-lg`}>
                  <Icon className="text-black/80" size={tall ? 26 : 22} />
                </div>
                <div className="text-[11px] font-semibold text-zinc-400">{st.label}</div>
                <div className="font-heading font-bold text-sm sm:text-base truncate max-w-full">{r.name}</div>
                <div className="text-emerald-400 font-bold mt-1 whitespace-nowrap overflow-hidden text-ellipsis max-w-full" style={{ fontSize: "clamp(0.7rem, 4vw, 1.05rem)", fontVariantNumeric: "tabular-nums" }} title={rupiah(r.omset_gabungan)}>{rupiah(r.omset_gabungan)}</div>
                {r.omset_produksi > 0 && <div className="text-[10px] text-purple-400">Design {rupiah(r.omset)} · Produksi {rupiah(r.omset_produksi)}</div>}
                <div className="text-[11px] text-zinc-500">{r.closing} closing · {r.closing_rate}%</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Full ranking */}
      {!isLoading && rows.length > 0 && (
        <div className="card-surface rounded-2xl overflow-hidden" data-testid="lb-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-zinc-500 border-b border-white/10">
                  <th className="px-3 py-3 w-12">#</th>
                  <th className="px-3 py-3">Sales</th>
                  <th className="px-3 py-3 text-right">Omset (Gabungan)</th>
                  <th className="px-3 py-3 text-right hidden sm:table-cell">Closing</th>
                  <th className="px-3 py-3 text-right hidden sm:table-cell">Konversi</th>
                  <th className="px-3 py-3 text-right hidden md:table-cell">Komisi (Gabungan)</th>
                  <th className="px-3 py-3 text-right">Target</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isMe = r.sales_id === user?.user_id;
                  return (
                    <tr key={r.sales_id} data-testid={`lb-row-${r.rank}`} className={`border-b border-white/5 last:border-0 ${isMe ? "bg-blue-500/10" : ""}`}>
                      <td className="px-3 py-3 font-bold text-zinc-400">{r.rank}</td>
                      <td className="px-3 py-3">
                        <div className="font-medium flex items-center gap-2">
                          {r.name}{isMe && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300">Anda</span>}
                        </div>
                        <div className="mt-1"><Badges badges={r.badges} /></div>
                      </td>
                      <td className="px-3 py-3 text-right font-semibold text-emerald-400">
                        {rupiah(r.omset_gabungan)}
                        {r.omset_produksi > 0 && <div className="text-[10px] font-normal text-purple-400">Produksi {rupiah(r.omset_produksi)}</div>}
                      </td>
                      <td className="px-3 py-3 text-right hidden sm:table-cell">{r.closing}</td>
                      <td className="px-3 py-3 text-right hidden sm:table-cell">{r.closing_rate}%</td>
                      <td className="px-3 py-3 text-right hidden md:table-cell text-zinc-400">{rupiah(r.commission_gabungan)}</td>
                      <td className="px-3 py-3 text-right">
                        {r.target_value > 0 ? (
                          <span className={r.achievement_pct >= 100 ? "text-emerald-400 font-semibold" : "text-zinc-400"}>{r.achievement_pct}%</span>
                        ) : <span className="text-zinc-600">-</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-xs text-zinc-600 mt-4 flex items-center gap-1.5">
        <Award size={13} /> Leaderboard hanya menampilkan ringkasan performa. Data & kontak client tiap sales tetap privat.
      </p>
    </div>
  );
}
